import { useCallback, useEffect, useState } from 'react';

const HINTS_KEY = 'sketchflow.hints-seen';

const HINTS = [
  { id: 'cmd-palette', text: 'Press ⌘K to open the command palette', delay: 3000 },
  { id: 'slash-commands', text: 'Type / in chat for quick diagram commands', delay: 8000 },
  { id: 'space-drag', text: 'Hold Space + drag to pan the canvas (Figma-style)', delay: 15000 },
  { id: 'shortcuts', text: 'Press ? to see all keyboard shortcuts', delay: 25000 },
];

function getSeenHints(): Set<string> {
  try {
    const raw = localStorage.getItem(HINTS_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function markSeen(id: string) {
  const seen = getSeenHints();
  seen.add(id);
  try {
    localStorage.setItem(HINTS_KEY, JSON.stringify([...seen]));
  } catch { /* quota */ }
}

export function HintToast() {
  const [visible, setVisible] = useState<{ id: string; text: string } | null>(null);

  const dismiss = useCallback(() => {
    if (visible) {
      markSeen(visible.id);
      setVisible(null);
    }
  }, [visible]);

  useEffect(() => {
    const seen = getSeenHints();
    const unseen = HINTS.filter((h) => !seen.has(h.id));
    if (!unseen.length) return;

    const next = unseen[0];
    const timer = window.setTimeout(() => setVisible(next), next.delay);
    return () => window.clearTimeout(timer);
  }, [visible]);

  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(dismiss, 6000);
    return () => window.clearTimeout(timer);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div className="ds-hint-toast" role="status" aria-live="polite">
      <span className="ds-hint-toast__text">{visible.text}</span>
      <button type="button" className="ds-hint-toast__close" onClick={dismiss} aria-label="Dismiss">
        &times;
      </button>
    </div>
  );
}
